import React from "react";
import { animated, useSpring, useTransition } from "react-spring";
import type { Theme } from "@material-ui/core";
import { Box, IconButton, Paper, Typography, useMediaQuery } from "@material-ui/core";
import Backdrop from "@material-ui/core/Backdrop";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { Close as CloseIcon } from "@eroad/icons/material";

import useWindowSize from "../hooks/useWindowSize";

const AnimatedBox = animated(Box);
const AnimatedPaper = animated(Paper);
const AnimatedIcon = animated(CloseIcon);
const AnimatedTypography = animated(Typography);

const useStyles = makeStyles((theme: Theme) => ({
  close: {
    position: "absolute",
    right: theme.spacing(0),
    top: theme.spacing(0),
  },
  titleText: {
    paddingTop: theme.spacing(2),
    marginLeft: theme.spacing(5),
  },
  panel: {
    minHeight: "100vh",
    zIndex: 5,
  },
  closeIcon: {
    position: "relative",
    width: theme.spacing(7),
    height: theme.spacing(8) + 2,
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
    cursor: "pointer",
  },
  backdrop: {
    zIndex: 1,
  },
}));

export enum Side {
  Left,
  Right,
}
interface OptionPanelProps {
  side: Side;
  title?: string;
  show: boolean;
  top?: number;
  children?: React.ReactNode;
  closeChild?: React.ReactNode;
  onToggle?: (open: boolean) => void;
}

const OptionPanel = ({ side, title, show, top, children, closeChild, onToggle }: OptionPanelProps): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme();
  const [width, height] = useWindowSize();
  const sm = useMediaQuery<Theme>(theme.breakpoints.down("sm"));
  const [display, setDisplay] = React.useState<boolean>(show);
  const menuOut = useTransition(display, {
    from: { left: -240 },
    enter: { left: 1 },
    leave: { left: -240 },
  });
  const [hover, setHover] = React.useState<boolean>(false);
  const [enlarge, setEnlarge] = useSpring(() => ({
    transform: hover ? "scale(1.1)" : "scale(1.0)",
    left: -4,
    right: -4,
  }));
  if (side === Side.Left) setEnlarge({ left: display ? -theme.spacing(4) : theme.spacing(4) });
  if (side === Side.Right) setEnlarge({ right: display ? -theme.spacing(4) : theme.spacing(4) });
  const paperConfig = {
    mass: 1,
    friction: 20,
    tension: 120,
  };
  const appear = useSpring({ config: paperConfig, to: { opacity: display ? 0 : 1 }, delay: 200 });
  React.useEffect(() => {
    if (onToggle) onToggle(display);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [display]);
  React.useEffect(() => setDisplay(show), [show, width, height]);
  return (
    <>
      {sm && <Backdrop open={display} onClick={() => setDisplay(false)} className={classes.backdrop} />}

      <AnimatedBox
        position="absolute"
        top={sm ? (top ?? 20) + 60 : (top ?? 20)}
        style={enlarge}
        data-testid="optionPanel"
      >
        <AnimatedPaper
          elevation={3}
          className={classes.closeIcon}
          onClick={() => setDisplay(true)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={appear}
        >
          {closeChild}
        </AnimatedPaper>
      </AnimatedBox>

      {menuOut(
        (style, item) =>
          item && (
            <AnimatedBox
              position="absolute"
              top={0}
              width={theme.spacing(60)}
              className={classes.panel}
              style={style}
              data-testid="optionPanel-open"
            >
              <AnimatedPaper elevation={3} square data-testid="optionPanel" style={{ height: "100vh" }}>
                {title && (
                  <AnimatedTypography variant="h4" gutterBottom className={classes.titleText}>
                    {title}
                  </AnimatedTypography>
                )}
                <IconButton aria-label="close" onClick={() => setDisplay(false)} className={classes.close}>
                  <AnimatedIcon />
                </IconButton>
                {children}
              </AnimatedPaper>
            </AnimatedBox>
          ),
      )}
    </>
  );
};

export default OptionPanel;
